// LocalFirstEngine: réplica por alcance + outbox de operaciones + vista
// calculada. El servidor es la autoridad: el motor envía intenciones y aplica
// lo que el servidor confirma.
//
// Ciclos:
//   - push: un grupo a la vez (single-flight), en orden. Un grupo enviado queda
//     en la outbox hasta que el pull trae su versión (confirmación por la misma
//     vía que todo lo demás).
//   - pull: por alcance, desde el cursor; foto inicial paginada que solo
//     reemplaza lo local al terminar.
//   - timbre: el transporte avisa la versión de cada alcance; si pasa el
//     cursor, se hace pull.

import { MAX_FAILURES } from "./merge";
import {
  soloLeader,
  systemClock,
  TransientError,
  type Clock,
  type LeaderElection,
  type LeaderMessage,
  type OutboxStore,
  type ReplicaStore,
  type SyncTransport,
} from "./ports";
import {
  PROTOCOL_VERSION,
  type DoorbellVersions,
  type Op,
  type OpGroup,
  type OpKind,
  type OpResult,
  type RecoveryItem,
  type RecoveryKind,
  type Row,
  type ScopeState,
  type SetDelta,
  type Values,
} from "./types";
import { viewRow, viewRows, type ViewRow } from "./view";

export interface EngineOptions {
  replica: ReplicaStore;
  outbox: OutboxStore;
  transport: SyncTransport;
  leader?: LeaderElection;
  clock?: Clock;
  protocolVersion?: number;
  /** Ids de operaciones, grupos y filas nuevas (uuid). */
  newId?: () => string;
  backoff?: { baseMs: number; maxMs: number };
  /** Validación extra (dev-mode). Lanza si la operación no es válida. */
  validateOp?: (op: Op) => void;
  /**
   * Vencimiento de la copia local (ms desde la última sincronización): pasado
   * este tiempo sin sincronizar, las filas de un alcance se borran del
   * dispositivo. Lo pendiente del usuario no se toca. Sin valor, no vence.
   */
  ttlMs?: number;
  /**
   * Prepara un grupo justo antes de enviarlo (p. ej. sube los archivos
   * guardados en el dispositivo y cambia sus referencias locales por las del
   * servidor). La outbox conserva el grupo original. Si lanza
   * `TransientError`, el envío se reintenta más tarde.
   */
  preparePush?: (group: OpGroup) => Promise<OpGroup>;
}

/** Cada cuánto se revisa el vencimiento de los alcances activos. */
const EXPIRY_CHECK_MS = 60_000;


export interface OpDraft {
  scope: string;
  kind: OpKind;
  /** Obligatorio en patch/delete; en create, si falta, se genera. */
  rowId?: string;
  set?: Values;
  sets?: Record<string, SetDelta>;
}

export type CallStatus = "pending" | "syncing" | "confirmed" | "rejected";

export interface MutationCall {
  groupId: string;
  rowIds: string[];
  /** Resuelve cuando el cambio quedó guardado en el dispositivo (ya está en la vista). */
  local: Promise<void>;
  /** Resuelve cuando el servidor lo aplicó; rechaza con el motivo si no. */
  server: Promise<OpResult[]>;
  status(): CallStatus;
}

export interface SyncStatus {
  online: boolean;
  leader: boolean;
  pending: number;
  recovery: number;
  paused: boolean;
}

export class ReadOnlyScopeError extends Error {
  constructor(scope: string) {
    super(`El alcance «${scope}» es de solo lectura sin conexión`);
    this.name = "ReadOnlyScopeError";
  }
}

export class SyncRejectedError extends Error {
  constructor(
    readonly kind: RecoveryKind,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "SyncRejectedError";
  }
}

interface ScopeRuntime {
  state: ScopeState;
  confirmed: Map<string, Row>;
  /** Foto inicial en curso (no reemplaza lo local hasta terminar). */
  staging: Map<string, Row> | null;
  pulling: boolean;
  pullAgain: boolean;
  view: ViewRow[] | null;
}

interface Deferred {
  promise: Promise<OpResult[]>;
  resolve: (results: OpResult[]) => void;
  reject: (error: Error) => void;
  status: CallStatus;
}

const DEFAULT_BACKOFF = { baseMs: 1000, maxMs: 30_000 };

export class LocalFirstEngine {
  private readonly replica: ReplicaStore;
  private readonly outbox: OutboxStore;
  private readonly transport: SyncTransport;
  private readonly leader: LeaderElection;
  private readonly clock: Clock;
  private readonly protocolVersion: number;
  private readonly newId: () => string;
  private readonly backoff: { baseMs: number; maxMs: number };
  private readonly validateOp?: (op: Op) => void;
  private readonly ttlMs?: number;
  private readonly preparePush?: (group: OpGroup) => Promise<OpGroup>;
  private expiryTimer: unknown = null;

  private readonly scopes = new Map<string, ScopeRuntime>();
  private groups: OpGroup[] = [];
  private recovery: RecoveryItem[] = [];
  private readonly deferreds = new Map<string, Deferred>();
  private readonly listeners = new Set<() => void>();
  private version = 0;
  private inFlight: string | null = null;
  private pushTimer: unknown = null;
  private readonly pullTimers = new Map<string, unknown>();
  private paused = false;
  private visible = true;
  private started = false;
  private disposed = false;
  private unsubscribeDoorbell: (() => void) | null = null;
  private readonly cleanups: Array<() => void> = [];

  constructor(options: EngineOptions) {
    this.replica = options.replica;
    this.outbox = options.outbox;
    this.transport = options.transport;
    this.leader = options.leader ?? soloLeader;
    this.clock = options.clock ?? systemClock;
    this.protocolVersion = options.protocolVersion ?? PROTOCOL_VERSION;
    this.newId = options.newId ?? (() => crypto.randomUUID());
    this.backoff = options.backoff ?? DEFAULT_BACKOFF;
    this.validateOp = options.validateOp;
    this.ttlMs = options.ttlMs;
    this.preparePush = options.preparePush;
  }

  // ── Ciclo de vida ──────────────────────────────────────────────────────

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;
    this.groups = sortGroups(await this.outbox.list());
    this.recovery = await this.outbox.listRecovery();
    this.cleanups.push(
      this.transport.onOnlineChange((online) => {
        if (online) this.kickAll();
        this.notify();
      }),
      this.leader.onLeaderChange((leader) => {
        if (leader) {
          this.resubscribe();
          this.kickAll();
        } else {
          this.unsubscribe();
        }
        this.notify();
      }),
      this.leader.onMessage((message) => void this.onLeaderMessage(message)),
    );
    this.notify();
    this.kickPush();
    this.scheduleExpiryCheck();
  }

  dispose(): void {
    this.disposed = true;
    this.unsubscribe();
    for (const cleanup of this.cleanups.splice(0)) cleanup();
    if (this.pushTimer) this.clock.clearTimeout(this.pushTimer);
    if (this.expiryTimer) this.clock.clearTimeout(this.expiryTimer);
    for (const timer of this.pullTimers.values()) this.clock.clearTimeout(timer);
    this.pullTimers.clear();
  }

  /** Activa un alcance: carga lo local, se suscribe al timbre y sincroniza. */
  async enableScope(scope: string, options: { readOnly?: boolean } = {}): Promise<void> {
    if (!this.scopes.has(scope)) {
      const { state, rows } = await this.replica.loadScope(scope);
      this.scopes.set(scope, {
        state: state ?? { scope, cursor: null, status: "hydrating", failures: 0 },
        confirmed: new Map(rows.map((row) => [row.id, row])),
        staging: null,
        pulling: false,
        pullAgain: false,
        view: null,
      });
    }
    const runtime = this.scopes.get(scope)!;
    this.expireIfStale(runtime);
    if (options.readOnly !== undefined) runtime.state = { ...runtime.state, readOnly: options.readOnly };
    if (runtime.state.status === "disabled") runtime.state = { ...runtime.state, status: statusFromCursor(runtime.state) };
    // Activarlo de nuevo (p. ej. la colección volvió a estar disponible) reintenta.
    if (runtime.state.status === "denied") this.restart(runtime);
    this.resubscribe();
    this.notify();
    void this.pullScope(scope);
  }

  /**
   * Desactiva un alcance: borra su copia local. Lo pendiente del usuario no se
   * descarta: se sigue enviando.
   */
  async disableScope(scope: string): Promise<void> {
    if (!this.scopes.has(scope)) return;
    this.scopes.delete(scope);
    await this.replica.clearScope(scope);
    this.resubscribe();
    this.notify();
  }

  setVisible(visible: boolean): void {
    if (this.visible === visible) return;
    this.visible = visible;
    // Oculta: deja de descargar (ahorro de batería y datos); la outbox sigue.
    if (visible) {
      this.resubscribe();
      this.kickAll();
    } else {
      this.unsubscribe();
    }
  }

  // ── Lectura ────────────────────────────────────────────────────────────

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Cambia con cada modificación observable (para useSyncExternalStore). */
  getVersion(): number {
    return this.version;
  }

  rows(scope: string): ViewRow[] {
    const runtime = this.scopes.get(scope);
    if (!runtime) return [];
    if (!runtime.view) runtime.view = viewRows(runtime.confirmed, this.opsFor(scope));
    return runtime.view;
  }

  row(scope: string, rowId: string): ViewRow | undefined {
    const runtime = this.scopes.get(scope);
    if (!runtime) return undefined;
    return viewRow(
      runtime.confirmed.get(rowId),
      this.opsFor(scope).filter((op) => op.rowId === rowId),
    );
  }

  /** Vista recalculada desde cero, sin caché (dev-mode: comprueba que sea determinista). */
  recomputeRows(scope: string): ViewRow[] {
    const runtime = this.scopes.get(scope);
    return runtime ? viewRows(runtime.confirmed, this.opsFor(scope)) : [];
  }

  /** Fila confirmada por el servidor (sin cambios pendientes). */
  confirmedRow(scope: string, rowId: string): Row | undefined {
    return this.scopes.get(scope)?.confirmed.get(rowId);
  }

  scopeState(scope: string): ScopeState | undefined {
    return this.scopes.get(scope)?.state;
  }

  /** Estado de cada alcance activo (avisos globales: detenidos, versión vieja). */
  scopeStates(): ScopeState[] {
    return [...this.scopes.values()].map((runtime) => runtime.state);
  }

  syncStatus(): SyncStatus {
    return {
      online: this.transport.isOnline(),
      leader: this.leader.isLeader(),
      pending: this.groups.length,
      recovery: this.recovery.length,
      paused: this.paused,
    };
  }

  recoveryItems(): readonly RecoveryItem[] {
    return this.recovery;
  }

  pendingGroups(): readonly OpGroup[] {
    return this.groups;
  }

  // ── Escritura ──────────────────────────────────────────────────────────

  commit(drafts: OpDraft[]): MutationCall {
    if (drafts.length === 0) throw new Error("Sin operaciones");
    const now = this.clock.now();
    const ops = drafts.map((draft) => this.toOp(draft, now));
    for (const op of ops) this.validateOp?.(op);

    const tail = this.groups[this.groups.length - 1];
    const coalesced = ops.length === 1 && tail && this.canCoalesce(tail, ops[0]!) ? this.coalesce(tail, ops[0]!) : null;
    const group: OpGroup = coalesced ?? {
      groupId: this.newId(),
      seq: (tail?.seq ?? 0) + 1,
      ops,
      createdAt: now,
      attempts: 0,
    };
    if (!coalesced) this.groups.push(group);
    const deferred = this.deferredFor(group.groupId);
    this.invalidate(ops.map((op) => op.scope));
    this.notify();

    const local = this.outbox.put(group).then(() => {
      this.leader.broadcast({ type: "outbox" });
      this.kickPush();
    });
    return {
      groupId: group.groupId,
      rowIds: ops.map((op) => op.rowId),
      local,
      server: deferred.promise,
      status: () => deferred.status,
    };
  }

  // ── Bandejas ───────────────────────────────────────────────────────────

  /**
   * Vuelve a intentar un grupo de la bandeja como un grupo nuevo, sobre la base
   * actual (el usuario ya vio lo que el servidor tiene).
   */
  retryRecovery(groupId: string): MutationCall | null {
    const item = this.recovery.find((r) => r.groupId === groupId);
    if (!item) return null;
    const drafts = item.group.ops.map((op) => ({
      scope: op.scope,
      kind: op.kind,
      rowId: op.rowId,
      set: op.set,
      sets: op.sets,
    }));
    void this.discardRecovery(groupId);
    return this.commit(drafts);
  }

  /** Conflicto de revisión: el usuario elige su valor para los campos indicados. */
  resolveConflict(groupId: string, keepLocal: string[]): MutationCall | null {
    const item = this.recovery.find((r) => r.groupId === groupId && r.kind === "conflict");
    if (!item) return null;
    void this.discardRecovery(groupId);
    if (keepLocal.length === 0) return null;
    const byRow = new Map<string, OpDraft>();
    for (const op of item.group.ops) {
      for (const field of keepLocal) {
        if (!op.set || !(field in op.set)) continue;
        const draft = byRow.get(op.rowId) ?? { scope: op.scope, kind: "patch" as const, rowId: op.rowId, set: {} };
        draft.set![field] = op.set[field];
        byRow.set(op.rowId, draft);
      }
    }
    return byRow.size > 0 ? this.commit([...byRow.values()]) : null;
  }

  async discardRecovery(groupId: string): Promise<void> {
    this.recovery = this.recovery.filter((r) => r.groupId !== groupId);
    await this.outbox.removeRecovery(groupId);
    this.leader.broadcast({ type: "recovery" });
    this.notify();
  }

  /** Reanuda un alcance detenido por el freno de reintentos. */
  resume(scope: string): void {
    const runtime = this.scopes.get(scope);
    if (!runtime) return;
    runtime.state = { ...runtime.state, failures: 0, status: statusFromCursor(runtime.state) };
    void this.replica.saveState(runtime.state);
    this.notify();
    void this.pullScope(scope);
    this.kickPush();
  }

  // ── Interno: operaciones ───────────────────────────────────────────────

  private toOp(draft: OpDraft, now: number): Op {
    const runtime = this.scopes.get(draft.scope);
    if (runtime?.state.readOnly) throw new ReadOnlyScopeError(draft.scope);
    if (draft.kind !== "create" && !draft.rowId) throw new Error(`${draft.kind} sin rowId`);
    const rowId = draft.rowId ?? this.newId();
    const confirmed = runtime?.confirmed.get(rowId);
    const touched = [...Object.keys(draft.set ?? {}), ...Object.keys(draft.sets ?? {})];
    const base: Record<string, number> = {};
    if (draft.kind === "patch") for (const field of touched) base[field] = confirmed?.fieldVersions[field] ?? 0;
    return {
      opId: this.newId(),
      scope: draft.scope,
      kind: draft.kind,
      rowId,
      ...(draft.set ? { set: { ...draft.set } } : {}),
      ...(draft.sets ? { sets: { ...draft.sets } } : {}),
      ...(draft.kind === "patch" ? { base } : {}),
      fieldsVersion: runtime?.state.fieldsVersion ?? 0,
      madeAt: now,
    };
  }

  /** Un patch más sobre la misma fila, en el último grupo aún sin enviar. */
  private canCoalesce(tail: OpGroup, op: Op): boolean {
    // Solo la líder sabe qué grupo está en vuelo: en otra pestaña, fusionar con
    // un grupo ya enviado perdería el cambio (el servidor repite por `opId`).
    if (!this.leader.isLeader()) return false;
    if (op.kind !== "patch" || tail.confirmAt || this.inFlight === tail.groupId) return false;
    if (tail.ops.length !== 1) return false;
    const last = tail.ops[0]!;
    return last.scope === op.scope && last.rowId === op.rowId && (last.kind === "patch" || last.kind === "create");
  }

  private coalesce(tail: OpGroup, op: Op): OpGroup {
    const last = tail.ops[0]!;
    const sets = { ...(last.sets ?? {}) };
    for (const [field, delta] of Object.entries(op.sets ?? {})) {
      const prev = sets[field];
      sets[field] = prev
        ? { add: [...(prev.add ?? []), ...(delta.add ?? [])], remove: [...(prev.remove ?? []), ...(delta.remove ?? [])], encoding: delta.encoding ?? prev.encoding }
        : delta;
    }
    // La base es la de la primera operación: lo que el usuario vio del servidor.
    const base = last.kind === "patch" ? { ...(op.base ?? {}), ...(last.base ?? {}) } : undefined;
    const merged: Op = {
      ...last,
      set: { ...(last.set ?? {}), ...(op.set ?? {}) },
      ...(Object.keys(sets).length > 0 ? { sets } : {}),
      ...(base ? { base } : {}),
      madeAt: op.madeAt,
    };
    tail.ops = [merged];
    return tail;
  }

  private opsFor(scope: string): Op[] {
    const ops: Op[] = [];
    for (const group of this.groups) for (const op of group.ops) if (op.scope === scope) ops.push(op);
    return ops;
  }

  private deferredFor(groupId: string): Deferred {
    const existing = this.deferreds.get(groupId);
    if (existing) return existing;
    let resolve!: (results: OpResult[]) => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<OpResult[]>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    // Nadie está obligado a esperar al servidor: sin esto, un rechazo sin
    // `await call.server` sería una promesa rechazada sin manejar.
    promise.catch(() => {});
    const deferred: Deferred = { promise, resolve, reject, status: "pending" };
    this.deferreds.set(groupId, deferred);
    return deferred;
  }

  // ── Interno: push ──────────────────────────────────────────────────────

  private kickPush(delayMs = 0): void {
    if (this.disposed || this.pushTimer) return;
    this.pushTimer = this.clock.setTimeout(() => {
      this.pushTimer = null;
      void this.pushNext();
    }, delayMs);
  }

  private async pushNext(): Promise<void> {
    if (this.disposed || this.paused || this.inFlight || !this.leader.isLeader() || !this.transport.isOnline()) return;
    const group = this.groups.find((g) => !g.confirmAt);
    if (!group) return;
    const scopes = [...new Set(group.ops.map((op) => op.scope))];
    if (scopes.some((s) => this.scopes.get(s)?.state.status === "stalled")) return;
    this.inFlight = group.groupId;
    const deferred = this.deferredFor(group.groupId);
    deferred.status = "syncing";
    this.notify();
    let retryIn: number | null = null;
    try {
      const outgoing = this.preparePush ? await this.preparePush(group) : group;
      const result = await this.transport.push(outgoing, this.protocolVersion);
      if (result.status === "ok") {
        await this.onPushed(group, result.results);
        deferred.status = "confirmed";
        deferred.resolve(result.results);
        this.leader.broadcast({ type: "pushed", groupId: group.groupId, results: result.results });
      } else if (result.status === "upgradeRequired") {
        this.pause();
      } else {
        await this.toRecovery(group, result.status === "staleSchema" ? "staleSchema" : "rejected", result.error);
      }
    } catch (error) {
      group.attempts += 1;
      deferred.status = "pending";
      const serverSide = !(error instanceof TransientError) || error.kind === "server";
      if (serverSide) for (const scope of scopes) this.countFailure(scope);
      retryIn = Math.min(this.backoff.baseMs * 2 ** (group.attempts - 1), this.backoff.maxMs);
    } finally {
      this.inFlight = null;
      this.notify();
    }
    if (retryIn !== null) this.kickPush(retryIn);
    else this.kickPush();
  }

  private async onPushed(group: OpGroup, results: OpResult[]): Promise<void> {
    const confirmAt: Record<string, number> = {};
    for (const op of group.ops) {
      const result = results.find((r) => r.opId === op.opId);
      if (result) confirmAt[op.scope] = Math.max(confirmAt[op.scope] ?? 0, result.version);
    }
    group.confirmAt = confirmAt;
    for (const scope of Object.keys(confirmAt)) this.resetFailures(scope);
    await this.outbox.put(group);
    const conflicts = results.flatMap((r) => r.conflicts ?? []);
    if (conflicts.length > 0) {
      await this.addRecovery({
        groupId: `${group.groupId}:conflict`,
        kind: "conflict",
        group,
        conflicts,
        at: this.clock.now(),
      });
    }
    // La confirmación llega por el pull, como todo lo demás.
    for (const scope of Object.keys(confirmAt)) void this.pullScope(scope);
    this.confirmGroups();
  }

  private async toRecovery(group: OpGroup, kind: RecoveryKind, error?: { code: string; message: string }): Promise<void> {
    this.groups = this.groups.filter((g) => g.groupId !== group.groupId);
    await this.outbox.remove(group.groupId);
    await this.addRecovery({ groupId: group.groupId, kind, group, error, at: this.clock.now() });
    const deferred = this.deferredFor(group.groupId);
    deferred.status = "rejected";
    deferred.reject(new SyncRejectedError(kind, error?.code ?? kind, error?.message ?? kind));
    this.leader.broadcast({ type: "rejected", groupId: group.groupId, kind, code: error?.code ?? kind, message: error?.message ?? kind });
    this.invalidate(group.ops.map((op) => op.scope));
    this.leader.broadcast({ type: "outbox" });
  }

  private async addRecovery(item: RecoveryItem): Promise<void> {
    this.recovery = [...this.recovery.filter((r) => r.groupId !== item.groupId), item];
    await this.outbox.putRecovery(item);
    this.leader.broadcast({ type: "recovery" });
  }

  /** Quita de la outbox los grupos cuyo resultado ya trajo el pull. */
  private confirmGroups(): void {
    const done = this.groups.filter(
      (g) =>
        g.confirmAt &&
        Object.entries(g.confirmAt).every(([scope, version]) => {
          const runtime = this.scopes.get(scope);
          // Alcance no activo: nada que esperar, el servidor ya lo aplicó.
          return !runtime || (runtime.state.cursor ?? -1) >= version;
        }),
    );
    if (done.length === 0) return;
    const ids = new Set(done.map((g) => g.groupId));
    this.groups = this.groups.filter((g) => !ids.has(g.groupId));
    for (const g of done) void this.outbox.remove(g.groupId);
    this.invalidate(done.flatMap((g) => g.ops.map((op) => op.scope)));
    this.leader.broadcast({ type: "outbox" });
    this.notify();
  }

  // ── Interno: pull ──────────────────────────────────────────────────────

  private async pullScope(scope: string): Promise<void> {
    const runtime = this.scopes.get(scope);
    if (!runtime || this.disposed || this.paused) return;
    if (runtime.pulling) {
      runtime.pullAgain = true;
      return;
    }
    if (!this.leader.isLeader() || !this.transport.isOnline()) return;
    if (runtime.state.status === "denied" || runtime.state.status === "stalled") return;
    runtime.pulling = true;
    let retryIn: number | null = null;
    try {
      let more = true;
      while (more && this.scopes.get(scope) === runtime && !this.paused) {
        const asked = { cursor: runtime.state.cursor, bootstrap: runtime.state.bootstrap };
        const result = await this.transport.pull({
          scope,
          cursor: asked.cursor,
          bootstrap: asked.bootstrap,
          protocolVersion: this.protocolVersion,
        });
        // El alcance se reinició mientras esperaba (caducidad, acceso
        // recuperado): la respuesta es de otro cursor y se vuelve a pedir.
        if (runtime.state.cursor !== asked.cursor || runtime.state.bootstrap !== asked.bootstrap) continue;
        if (result.status === "denied") {
          await this.onDenied(scope);
          return;
        }
        if (result.status === "upgradeRequired") {
          this.pause();
          return;
        }
        if (result.status === "resetRequired") {
          runtime.state = { ...runtime.state, cursor: null, bootstrap: undefined, status: "hydrating" };
          runtime.staging = null;
          continue;
        }
        await this.applyPull(runtime, result);
        more = result.hasMore;
      }
      this.resetFailures(scope);
    } catch (error) {
      const serverSide = !(error instanceof TransientError) || error.kind === "server";
      if (serverSide) this.countFailure(scope);
      retryIn = this.backoff.baseMs * 2 ** Math.min(runtime.state.failures, 5);
    } finally {
      runtime.pulling = false;
      this.confirmGroups();
      this.notify();
    }
    if (retryIn !== null && runtime.state.status !== "stalled") this.schedulePull(scope, Math.min(retryIn, this.backoff.maxMs));
    else if (runtime.pullAgain) {
      runtime.pullAgain = false;
      void this.pullScope(scope);
    }
  }

  private async applyPull(runtime: ScopeRuntime, result: Extract<Awaited<ReturnType<SyncTransport["pull"]>>, { status: "ok" }>): Promise<void> {
    const bootstrapping = runtime.state.cursor === null;
    const base = {
      ...runtime.state,
      fieldsVersion: result.fieldsVersion ?? runtime.state.fieldsVersion,
      ...(result.readOnly !== undefined ? { readOnly: result.readOnly } : {}),
    };
    if (bootstrapping) {
      runtime.staging ??= new Map();
      for (const row of result.rows) runtime.staging.set(row.id, row);
      for (const id of result.deletes) runtime.staging.delete(id);
      if (result.hasMore) {
        runtime.state = { ...base, bootstrap: result.bootstrap, status: "hydrating" };
        return;
      }
      // Foto completa: recién ahora reemplaza lo local (una interrupción no pierde datos).
      const staging = runtime.staging;
      runtime.staging = null;
      const state: ScopeState = {
        ...base,
        cursor: result.cursor,
        bootstrap: undefined,
        status: result.partial ? "partial" : "hydrated",
        syncedAt: this.clock.now(),
        expiredAt: undefined,
        failures: 0,
      };
      await this.replica.applyPage(runtime.state.scope, { upserts: [...staging.values()], deletes: [], replaceAll: true, state });
      runtime.confirmed = staging;
      runtime.state = keepLocal(state, runtime.state, result.readOnly);
    } else {
      const state: ScopeState = {
        ...base,
        cursor: result.cursor,
        status: runtime.state.status === "partial" ? "partial" : "hydrated",
        syncedAt: this.clock.now(),
        expiredAt: undefined,
        failures: 0,
      };
      await this.replica.applyPage(runtime.state.scope, { upserts: result.rows, deletes: result.deletes, state });
      for (const row of result.rows) runtime.confirmed.set(row.id, row);
      for (const id of result.deletes) runtime.confirmed.delete(id);
      runtime.state = keepLocal(state, runtime.state, result.readOnly);
    }
    runtime.view = null;
    this.leader.broadcast({ type: "replica", scope: runtime.state.scope });
  }

  /**
   * Copia local vieja (TTL): con red, se refresca con un pull (que renueva
   * `syncedAt`, aunque no haya cambios); sin red, se borra del dispositivo. Lo
   * pendiente del usuario queda en la outbox.
   */
  private expireIfStale(runtime: ScopeRuntime): boolean {
    const { state } = runtime;
    if (!this.ttlMs || state.cursor === null || !state.syncedAt) return false;
    const now = this.clock.now();
    if (now - state.syncedAt <= this.ttlMs) return false;
    if (this.transport.isOnline()) {
      void this.pullScope(state.scope);
      return false;
    }
    runtime.confirmed = new Map();
    runtime.staging = null;
    runtime.view = null;
    runtime.state = { scope: state.scope, cursor: null, status: "hydrating", failures: 0, readOnly: state.readOnly, expiredAt: now };
    void this.replica.clearScope(state.scope).then(() => this.replica.saveState(runtime.state));
    this.notify();
    return true;
  }

  private scheduleExpiryCheck(): void {
    if (!this.ttlMs || this.disposed) return;
    this.expiryTimer = this.clock.setTimeout(() => {
      this.expiryTimer = null;
      for (const runtime of this.scopes.values()) this.expireIfStale(runtime);
      this.scheduleExpiryCheck();
    }, Math.min(EXPIRY_CHECK_MS, this.ttlMs));
  }

  /** Alcance que vuelve a bajarse desde la foto inicial (tras recuperar el acceso). */
  private restart(runtime: ScopeRuntime): void {
    runtime.state = { ...runtime.state, cursor: null, bootstrap: undefined, status: "hydrating", failures: 0 };
    void this.replica.saveState(runtime.state);
    this.notify();
  }

  /** Sin acceso: se borra la copia local y lo pendiente pasa a la bandeja. */
  private async onDenied(scope: string): Promise<void> {
    const runtime = this.scopes.get(scope);
    if (!runtime) return;
    runtime.confirmed = new Map();
    runtime.staging = null;
    runtime.state = { scope, cursor: null, status: "denied", failures: 0 };
    await this.replica.clearScope(scope);
    await this.replica.saveState(runtime.state);
    for (const group of this.groups.filter((g) => g.ops.some((op) => op.scope === scope))) {
      await this.toRecovery(group, "denied", { code: "denied", message: "Ya no tienes acceso a esta colección" });
    }
    runtime.view = null;
    this.notify();
  }

  private schedulePull(scope: string, ms: number): void {
    if (this.pullTimers.has(scope) || this.disposed) return;
    this.pullTimers.set(
      scope,
      this.clock.setTimeout(() => {
        this.pullTimers.delete(scope);
        void this.pullScope(scope);
      }, ms),
    );
  }

  // ── Interno: freno, pausa, timbre ──────────────────────────────────────

  private countFailure(scope: string): void {
    const runtime = this.scopes.get(scope);
    if (!runtime) return;
    const failures = runtime.state.failures + 1;
    runtime.state = { ...runtime.state, failures, ...(failures >= MAX_FAILURES ? { status: "stalled" as const } : {}) };
    void this.replica.saveState(runtime.state);
  }

  private resetFailures(scope: string): void {
    const runtime = this.scopes.get(scope);
    if (!runtime || runtime.state.failures === 0) return;
    runtime.state = { ...runtime.state, failures: 0 };
  }

  /** El servidor no acepta este cliente: se pausa sin descartar nada. */
  private pause(): void {
    this.paused = true;
    for (const runtime of this.scopes.values()) runtime.state = { ...runtime.state, status: "upgradeRequired" };
    this.notify();
  }

  private kickAll(): void {
    for (const scope of this.scopes.keys()) void this.pullScope(scope);
    this.kickPush();
  }

  private resubscribe(): void {
    this.unsubscribe();
    if (!this.visible || !this.leader.isLeader() || this.disposed) return;
    const scopes = [...this.scopes.keys()];
    if (scopes.length === 0) return;
    this.unsubscribeDoorbell = this.transport.subscribe(scopes, (versions) => this.onDoorbell(versions));
  }

  private unsubscribe(): void {
    this.unsubscribeDoorbell?.();
    this.unsubscribeDoorbell = null;
  }

  private onDoorbell(versions: DoorbellVersions): void {
    for (const [scope, version] of Object.entries(versions)) {
      const runtime = this.scopes.get(scope);
      if (!runtime) continue;
      if (version === "denied") {
        if (runtime.state.status !== "denied") void this.onDenied(scope);
      } else if (runtime.state.status === "denied") {
        // Volvió el acceso: se baja de nuevo desde cero.
        this.restart(runtime);
        void this.pullScope(scope);
      } else if (runtime.state.cursor === null || version > runtime.state.cursor) void this.pullScope(scope);
    }
  }

  private async onLeaderMessage(message: LeaderMessage): Promise<void> {
    if (this.leader.isLeader() && message.type !== "outbox") return;
    // Un cambio de esta pestaña que envió la líder: su `call.server` termina aquí.
    if (message.type === "pushed" || message.type === "rejected") {
      const deferred = this.deferreds.get(message.groupId);
      if (!deferred || deferred.status === "confirmed" || deferred.status === "rejected") return;
      if (message.type === "pushed") {
        deferred.status = "confirmed";
        deferred.resolve(message.results);
      } else {
        deferred.status = "rejected";
        deferred.reject(new SyncRejectedError(message.kind, message.code, message.message));
      }
      return;
    }
    if (message.type === "outbox") {
      this.groups = sortGroups(await this.outbox.list());
      for (const runtime of this.scopes.values()) runtime.view = null;
      if (this.leader.isLeader()) this.kickPush();
    } else if (message.type === "recovery") {
      this.recovery = await this.outbox.listRecovery();
    } else if (message.type === "replica" && message.scope) {
      const runtime = this.scopes.get(message.scope);
      if (runtime) {
        const { state, rows } = await this.replica.loadScope(message.scope);
        runtime.confirmed = new Map(rows.map((row) => [row.id, row]));
        if (state) runtime.state = state;
        runtime.view = null;
      }
    }
    this.notify();
  }

  private invalidate(scopes: string[]): void {
    for (const scope of new Set(scopes)) {
      const runtime = this.scopes.get(scope);
      if (runtime) runtime.view = null;
    }
  }

  private notify(): void {
    this.version += 1;
    for (const listener of this.listeners) listener();
  }
}

/**
 * Estado tras escribir una página: lo que se decidió localmente mientras la
 * escritura estaba en vuelo (p. ej. activar solo lectura) no se pisa con la
 * copia tomada antes. Lo que mande el servidor gana.
 */
function keepLocal(next: ScopeState, current: ScopeState, serverReadOnly: boolean | undefined): ScopeState {
  return { ...next, readOnly: serverReadOnly ?? current.readOnly };
}

function sortGroups(groups: OpGroup[]): OpGroup[] {
  return [...groups].sort((a, b) => a.seq - b.seq);
}

function statusFromCursor(state: ScopeState): ScopeState["status"] {
  return state.cursor === null ? "hydrating" : "hydrated";
}
